import React, { useState, useEffect } from 'react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, 
  LineChart, Line, PieChart, Pie, Cell, ResponsiveContainer
} from 'recharts';
import { 
  AlertTriangle, ArrowUp, ArrowDown, Activity,
  Clock, Users, Calendar, Filter
} from 'lucide-react';

const API_BASE_URL = 'https://ior.org/risk-register/web/api';

const MetricCard = ({ title, value, change, description, icon: Icon }) => {
  const isPositiveChange = parseFloat(change) >= 0;
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex justify-between items-start">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <h3 className="text-2xl font-bold mt-2">{value}</h3>
        </div>
        <div className="p-2 bg-blue-50 rounded-lg">
          <Icon className="w-6 h-6 text-blue-600" />
        </div>
      </div>
      <div className="mt-4">
        <span className={`inline-flex items-center text-sm ${
          isPositiveChange ? 'text-green-600' : 'text-red-600'
        }`}>
          {isPositiveChange ? (
            <ArrowUp className="w-4 h-4 mr-1" />
          ) : (
            <ArrowDown className="w-4 h-4 mr-1" />
          )}
          {Math.abs(change)}%
        </span>
        <span className="text-sm text-gray-500 ml-2">vs last period</span>
      </div>
      <p className="mt-2 text-sm text-gray-600">{description}</p>
    </div>
  );
};

const RisksByCategory = ({ data }) => (
  <div className="bg-white rounded-lg shadow p-6">
    <h3 className="text-lg font-medium mb-6">Risks by Category</h3>
    <div className="h-[300px]">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" />
          <YAxis />
          <Tooltip />
          <Bar dataKey="value" fill="#3B82F6" />
        </BarChart>
      </ResponsiveContainer>
    </div>
  </div>
);

const RiskTrendChart = ({ data }) => (
  <div className="bg-white rounded-lg shadow p-6">
    <h3 className="text-lg font-medium mb-6">Risk Trend</h3>
    <div className="h-[300px]">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="date" />
          <YAxis />
          <Tooltip />
          <Legend />
          <Line type="monotone" dataKey="high" stroke="#EF4444" name="High Risk" />
          <Line type="monotone" dataKey="medium" stroke="#F59E0B" name="Medium Risk" />
          <Line type="monotone" dataKey="low" stroke="#10B981" name="Low Risk" />
        </LineChart>
      </ResponsiveContainer>
    </div>
  </div>
);

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#6366F1'];

const ApprovalsPieChart = ({ data }) => (
  <div className="bg-white rounded-lg shadow p-6">
    <h3 className="text-lg font-medium mb-6">Risk Approvals</h3>
    <div className="h-[300px]">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            innerRadius={60}
            outerRadius={80}
            fill="#8884d8"
            paddingAngle={5}
            dataKey="value"
          >
            {data.map((entry, index) => (
              <Cell key={entry.name} fill={COLORS[index % COLORS.length]} />
            ))}
          </Pie>
          <Tooltip />
          <Legend />
        </PieChart>
      </ResponsiveContainer>
    </div>
  </div>
);

const RiskAnalyticsDashboard = () => {
  const [metrics, setMetrics] = useState({
    totalRisks: { value: 0, change: 0 },
    highRisks: { value: 0, change: 0 },
    pendingApprovals: { value: 0, change: 0 },
    mitigationRate: { value: 0, change: 0 }
  });
  const [categoryData, setCategoryData] = useState([]);
  const [trendData, setTrendData] = useState([]);
  const [approvalData, setApprovalData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('30days');

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await fetch(
          `${API_BASE_URL}/risk-analytics.php?timeRange=${timeRange}`
        );
        const data = await response.json();
        
        if (data.success) {
          setMetrics(data.metrics);
          setCategoryData(data.categoryDistribution);
          setTrendData(data.riskTrend);
          setApprovalData(data.approvalStatus);
        } else {
          throw new Error(data.error || 'Failed to fetch analytics data');
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
  }, [timeRange]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold mb-2">Risk Analytics Dashboard</h1>
        <div className="flex justify-between items-center">
          <div className="text-gray-600">
            Comprehensive overview of risk management metrics
          </div>
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <select
              className="pl-10 pr-4 py-2 border rounded-md appearance-none bg-white"
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
            >
              <option value="7days">Last 7 Days</option>
              <option value="30days">Last 30 Days</option>
              <option value="90days">Last 90 Days</option>
              <option value="1year">Last Year</option>
            </select>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-400 text-red-700">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 mr-2" />
            {error}
          </div>
        </div>
      )}

      {/* Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        <MetricCard
          title="Total Risks"
          value={metrics.totalRisks.value}
          change={metrics.totalRisks.change}
          description="Active risks in the system"
          icon={AlertTriangle}
        />
        <MetricCard
          title="High Risk Items"
          value={metrics.highRisks.value}
          change={metrics.highRisks.change}
          description="Risks requiring immediate attention"
          icon={Activity}
        />
        <MetricCard
          title="Pending Approvals"
          value={metrics.pendingApprovals.value}
          change={metrics.pendingApprovals.change}
          description="Risks awaiting review"
          icon={Clock}
        />
        <MetricCard
          title="Mitigation Rate"
          value={`${metrics.mitigationRate.value}%`}
          change={metrics.mitigationRate.change}
          description="Risks successfully mitigated"
          icon={Users}
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <RisksByCategory data={categoryData} />
        <RiskTrendChart data={trendData} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ApprovalsPieChart data={approvalData} />
        {/* Additional chart or table could go here */}
      </div>
    </div>
  );
};

export default RiskAnalyticsDashboard;
